import React, { useState } from 'react';
import { ChevronLeftUniversal, OpenFolderOutlined } from '@duxton-web/icons';
import {
  Alert,
  Button,
  Checkbox,
  Modal,
  notification,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import { useQueryClient } from '@tanstack/react-query';

import { VariableState } from '@/constants';
import { useVariableContext } from '@/features/variable/contexts/variable-context';
import { useArchiveVariable } from '@/hooks';

const ModalTitle = ({
  isLoading,
  onClose,
}: {
  isLoading: boolean;
  onClose: () => void;
}) => {
  const { variable } = useVariableContext();

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
      <Button
        data-testid="delete-and-archive-back"
        shape="circle"
        ghost
        onClick={onClose}
        size="small"
        icon={<ChevronLeftUniversal />}
        disabled={isLoading}
      />
      <Tooltip title={`Delete Rollout and Archive Variable ${variable?.name}`}>
        <Typography.Title level={3} ellipsis>
          Delete Rollout and Archive Variable {variable?.name}
        </Typography.Title>
      </Tooltip>
    </div>
  );
};

const Header = () => {
  const { variable } = useVariableContext();

  const isActiveVariable = variable?.state === VariableState.ACTIVE;
  if (isActiveVariable)
    return (
      <Alert
        type="warning"
        message={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div>
              <Typography.Text strong style={{ color: '#3D3D3D' }}>
                Archiving an active variable
              </Typography.Text>
              <Typography.Text style={{ color: '#707070' }}>
                This variable was last triggered over the last 3 months.
              </Typography.Text>
            </div>
            <Typography.Text style={{ color: '#707070' }}>
              After clicking Archive, all values returned from GetVariable call
              will resolve to Code Default in about 2 mins if no experiment is
              running on this variable.
            </Typography.Text>
          </div>
        }
      />
    );

  return (
    <Typography.Text>
      After clicking Archive, all values returned from GetVariable call will
      resolve to Code Default in about 2 mins if no experiment is running on
      this variable. There is no recent exposures for this variable.
    </Typography.Text>
  );
};

const DeleteAndArchiveModal = ({
  open,
  onCancel = () => {},
  onOk = () => {},
}: {
  open: boolean;
  onCancel?: () => void;
  onOk?: () => void;
}) => {
  const queryClient = useQueryClient();

  const { variable } = useVariableContext();

  const variableName = variable?.name;

  const [isAcknowledged, setIsAcknowledged] = useState<boolean>(false);

  const handleCancel = () => {
    setIsAcknowledged(false);

    onCancel();
  };

  const { isPending, mutate } = useArchiveVariable({
    onSuccess: () => {
      notification.success({
        message: 'Archiving the Variable',
        description: `Variable ${variable?.name} was successfully archived.`,
      });

      queryClient.invalidateQueries({
        queryKey: ['planner', 'variable'],
      });

      setIsAcknowledged(false);

      onOk();
    },
    onError: (err: any) => {
      const errorData = err?.data?.response?.data;
      notification.error({
        message: 'Archiving variable failed',
        description: (
          <>
            <div>Failed to Archive the variable</div>
            {errorData?.arg && <div>Error: {errorData.arg}</div>}
          </>
        ),
      });
    },
  });

  return (
    <Modal
      data-testid="delete-and-archive-modal"
      closable={false}
      open={open}
      mask={false}
      centered
      maskClosable={false}
      title={<ModalTitle isLoading={isPending} onClose={handleCancel} />}
      onCancel={handleCancel}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
          <Button size="small" disabled={isPending} onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            size="small"
            danger
            disabled={!isAcknowledged || isPending}
            onClick={() => {
              mutate(variableName);
            }}
          >
            Archive
          </Button>
        </div>
      }
      width={700}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Header />
        <Typography.Text>
          Are you sure that you want to archive variable &quot;
          {variable?.name}&quot; and delete all the rollout on this variable?
        </Typography.Text>
        <Checkbox
          checked={isAcknowledged}
          onChange={() => setIsAcknowledged(!isAcknowledged)}
          style={{
            background: '#FFF',
            borderRadius: 12,
            borderColor: '#E0E0E0',
            borderWidth: '0.4px',
            padding: '12px 0px',
          }}
          disabled={isPending}
        >
          I understand I am archiving a variable and have checked with
          developers that code reference to this variable has been removed
        </Checkbox>
      </div>
    </Modal>
  );
};

const DeleteAndArchive = ({
  onDeleteAndArchiveClick = () => {},
  onDeleteAndArchiveCancel = () => {},
  onDeleteAndArchiveOk = () => {},
}: {
  onDeleteAndArchiveClick?: () => void;
  onDeleteAndArchiveCancel?: () => void;
  onDeleteAndArchiveOk?: () => void;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        data-testid="delete-and-archive-button"
        shape="round"
        icon={<OpenFolderOutlined />}
        size="small"
        danger
        onClick={() => {
          setOpen(true);

          onDeleteAndArchiveClick();
        }}
      >
        Delete Rollout and Archive
      </Button>
      <DeleteAndArchiveModal
        open={open}
        onCancel={() => {
          setOpen(false);

          onDeleteAndArchiveCancel();
        }}
        onOk={() => {
          setOpen(false);

          onDeleteAndArchiveOk();
        }}
      />
    </>
  );
};

export default DeleteAndArchive;
